/**
 * The cut-to-size modal's text in English, French and Spanish: labels, the field names of every
 * cut field, the dimension error messages, and the "this item cannot be cut" message.
 *
 * Read by `DefineCutModal.tsx` (falling back to `en`), which passes it to the desktop and mobile
 * views, and by the validators in `DefineCutHelper.ts`.
 *
 * Rules a change must keep:
 * - `cutAttributes.*.value` is a cut field key, not text. It must be the same in every language
 *   and equal `DefineCutHelper.ts` → `AttributesName`; the modal matches fields by it.
 * - The numbers in the error messages restate limits that live in code. Change both together:
 *   - 2 inches below stock size: `DefineCutHelper.ts` (sheet / plate rules) and
 *     `DefineCutModal.tsx` → `validateCutParameters` (square side, outside diameter).
 *   - 18 / 300 inches: `DefineCutHelper.ts` → `coilHasValidProperty`.
 *   - 3 inches and 1 inch outside diameter: `DefineCutModal.tsx` → `validateCutParameters`.
 *   - 2× plate thickness and the 1-inch ring wall: `DefineCutHelper.ts` →
 *     `odAndIdDifferenceValid`.
 *   - The bar, tube, pipe and structural length limit, the longer size of a two-size length:
 *     `DefineCutHelper.ts` → `longFormHasValidProperty`, which appends the number in inches to
 *     `errors.longForm`.
 * - `brandedEmail` is read from `getBrandInfo()` once, when this module loads.
 *
 * Nothing reads the top-level `primaryGrade`, `thickness`, `width`, `length`, `type` and `finish`
 * keys, or `errors.required`.
 */
import { getBrandInfo, LanguageContent } from "@ryerson/frontend.common";

/** A cut field: `value` is its key (the same in every language), `display` its label. */
type DefineCutAttributeType = {
	value: string;
	display: string;
};

/** The modal's text for one language. */
export interface DefineCutText {
	cutToSize: string;
	primaryGrade: string;
	thickness: string;
	width: string;
	length: string;
	type: string;
	finish: string;
	apply: string;
	back: string;
	select: string;
	cutTo: string;
	cutFrom: string;
	quantityPcs: string;
	quantityTooltipMessage: string;
	cutAttributes: {
		shape: DefineCutAttributeType;
		processing: DefineCutAttributeType;
		finish: DefineCutAttributeType;
		width: DefineCutAttributeType;
		length: DefineCutAttributeType;
		oneSide: DefineCutAttributeType;
		oneSideTolerance: DefineCutAttributeType;
		tolerance: DefineCutAttributeType;
		outsideDiameter: DefineCutAttributeType;
		outsideTolerance: DefineCutAttributeType;
		insideDiameter: DefineCutAttributeType;
		insideTolerance: DefineCutAttributeType;
		longSide: DefineCutAttributeType;
		shortSide: DefineCutAttributeType;
		widthLengthTolerance: DefineCutAttributeType;
	};
	errors: {
		required: string;
		outsideDiameter: {
			lessThanPlateWidth: string;
			minimumForOxyFuel: string;
			minimumForLaserCut: string;
		};
		insideDiameter: {
			lessThanOutside: string;
			twoTimesPlateThickness: string;
			thickerWallSize: string;
		};
		oneSide: {
			lessThanPlateWidth: string;
		};
		sheet: {
			sheetWidth: string;
			sheetLength: string;
		};
		/**
		 * Shown when a bar, tube, pipe or structural length is over the limit. The limit is the
		 * longer size of a two-size length, but all three languages call it the "minimum" length of
		 * the part; that word is a known defect.
		 */
		longForm: string;
		/**
		 * The "cannot be cut" message is assembled in the views as `processMessage1`, a link to
		 * `DefineCutHelper.ts` → `getCompanyLink` (text `companyLink` with `%url%` replaced),
		 * `processMessage2`, a mailto link to `brandedEmail`, then `processMessage3`.
		 */
		processMessage1: string;
		processMessage2: string;
		processMessage3: string;
		companyLink: string;
		brandedEmail?: string;
		/** The coil length message (18 to 300 inches). */
		length: string;
	};
}

/** The modal's text by language. */
export const DefineCutContent: LanguageContent<DefineCutText> = {
	en: {
		cutToSize: "Cut to Size",
		primaryGrade: "Primary Grade",
		thickness: "Thickness",
		width: "Width",
		length: "Length",
		type: "Type",
		finish: "Finish",
		apply: "Apply",
		back: "Back",
		select: "Select",
		cutTo: "Cut To:",
		cutFrom: "Cut From:",
		quantityPcs: "Quantity, pc(s)",
		quantityTooltipMessage:
			"Please indicate your final cut size and total number of pieces needed for this item.",
		cutAttributes: {
			shape: {
				value: "cutShape",
				display: "Shape",
			},
			processing: {
				value: "processing",
				display: "Process",
			},
			finish: {
				value: "finish",
				display: "Finish",
			},
			width: {
				value: "width",
				display: "Width",
			},
			length: {
				value: "length",
				display: "Length",
			},
			oneSide: {
				value: "oneSide",
				display: "Side",
			},
			oneSideTolerance: {
				value: "oneSideTolerance",
				display: "One Side Tolerance",
			},
			tolerance: {
				value: "tolerance",
				display: "Tolerance",
			},
			outsideDiameter: {
				value: "outsideDiameter",
				display: "Outside Diameter",
			},
			outsideTolerance: {
				value: "outsideTolerance",
				display: "Outside Tolerance",
			},
			insideDiameter: {
				value: "insideDiameter",
				display: "Inside Diameter",
			},
			insideTolerance: {
				value: "insideTolerance",
				display: "Inside Tolerance",
			},
			longSide: {
				value: "longSide",
				display: "Long Side",
			},
			shortSide: {
				value: "shortSide",
				display: "Short Side",
			},
			widthLengthTolerance: {
				value: "widthLengthTolerance",
				display: "Width/Length Tolerance",
			},
		},
		errors: {
			required: "Field is required",
			outsideDiameter: {
				lessThanPlateWidth:
					"Requested outside diameter must be less than or equal to the width of the stock plate minus 2 inches to process",
				minimumForOxyFuel:
					"Diameter must be greater than or equal to 3 inches for OxyFuel and Plasma cut parts",
				minimumForLaserCut:
					"Requested diameter must be greater than or equal to 1 inch for laser cut items",
			},
			insideDiameter: {
				lessThanOutside:
					"Requested inside diameter must be less than the requested outside diameter",
				twoTimesPlateThickness:
					"The difference between the OD and ID must be two times (2X) the plate thickness",
				thickerWallSize:
					"Please update OD or ID for a thicker wall size. The minimum ring wall thickness (difference between OD and ID) is 1”",
			},
			oneSide: {
				lessThanPlateWidth:
					"Requested width must be stock width or less than / equal to stock width minus 2 inches to process",
			},
			sheet: {
				sheetWidth:
					"Requested width must be stock width or less than / equal to stock width minus 2 inches to process",
				sheetLength:
					"Requested length must be stock length or less than / equal to stock length minus 2 inches to process",
			},
			longForm:
				"Requested length must be less than or equal to the minimum length of the part",
			processMessage1: "Thank you for using ",
			processMessage2: " This item cannot be cut at this time, please contact ",
			processMessage3: " or live chat for assistance.",
			companyLink: "%url%.",
		 
			brandedEmail: getBrandInfo().email,
			length: "Please specify a cut less than the maximum cut length (Minimum Length 18, Maximum Length 300)",
		},
	},
	fr: {
		cutToSize: "Coupe sur mesure",
		primaryGrade: "Qualité primaire",
		thickness: "Épaisseur",
		width: "Largeur",
		length: "Longitud",
		type: "Type",
		finish: "Finition",
		apply: "Appliquer",
		back: "Retour ",
		select: "Sélectionner",
		cutTo: "Couper jusqu’à :",
		cutFrom: "Couper à partir de:",
		quantityPcs: "Quantité, pièce(s)",
		quantityTooltipMessage:
			"Veuillez indiquer la dimension finale de la coupe et le nombre total de pièces nécessaires pour cet article.",
		cutAttributes: {
			shape: {
				value: "cutShape",
				display: "Forme",
			},
			processing: {
				value: "processing",
				display: "Processus",
			},
			finish: {
				value: "finish",
				display: "Finition",
			},
			width: {
				value: "width",
				display: "Largeur",
			},
			length: {
				value: "length",
				display: "Longueur",
			},
			oneSide: {
				value: "oneSide",
				display: "Sur le côté",
			},
			oneSideTolerance: {
				value: "oneSideTolerance",
				display: "Tolérance unilatérale",
			},
			tolerance: {
				value: "tolerance",
				display: "Tolérance",
			},
			outsideDiameter: {
				value: "outsideDiameter",
				display: "Diamètre extérieur",
			},
			outsideTolerance: {
				value: "outsideTolerance",
				display: "Tolérance extérieure",
			},
			insideDiameter: {
				value: "insideDiameter",
				display: "Diamètre intérieur",
			},
			insideTolerance: {
				value: "insideTolerance",
				display: "Tolérance intérieure",
			},
			longSide: {
				value: "longSide",
				display: "Côté long",
			},
			shortSide: {
				value: "shortSide",
				display: "Côté court",
			},
			widthLengthTolerance: {
				value: "widthLengthTolerance",
				display: "Tolérance largeur/longueur",
			},
		},
		errors: {
			required: "Le champ est obligatoire.",
			outsideDiameter: {
				lessThanPlateWidth:
					"Le diamètre extérieur demandé doit être inférieur ou égal à la largeur de la plaque, moins 2 pouces pour être traité.",
				minimumForOxyFuel:
					"Le diamètre doit être supérieur ou égal à 3 pouces pour les pièces découpées au plasma et à l’oxygaz.",
				minimumForLaserCut:
					"Le diamètre demandé doit être supérieur ou égal à 1 pouce pour les pièces découpées au laser.",
			},
			insideDiameter: {
				lessThanOutside:
					"Le diamètre intérieur demandé doit être inférieur au diamètre extérieur demandé.",
				twoTimesPlateThickness:
					"La différence entre le diamètre extérieur et le diamètre intérieur doit être égale à deux fois (2 x) l’épaisseur de la plaque.",
				thickerWallSize:
					"Veuillez mettre à jour le diamètre extérieur ou intérieur pour obtenir une paroi plus épaisse. L’épaisseur minimale de l’anneau (différence entre le diamètre extérieur et le diamètre intérieur) est de 1 pouce.",
			},
			oneSide: {
				lessThanPlateWidth:
					"La largeur demandée doit correspondre à la largeur de la plaque ou être inférieure ou égale à la largeur de la plaque, moins 2 pouces pour être traitée.",
			},
			sheet: {
				sheetWidth:
					"La largeur demandée doit correspondre à la largeur de la plaque ou être inférieure ou égale à la largeur de la plaque, moins 2 pouces pour être traitée.",
				sheetLength:
					"La longueur demandée doit correspondre à la longueur de la plaque ou être inférieure ou égale à la longueur de la plaque, moins 2 pouces pour être traitée.",
			},
			longForm:
				"La longueur demandée doit être inférieure ou égale à la longueur minimale de la pièce",
			processMessage1: "Merci d’utiliser ",
			processMessage2:
				". Cet item ne peut être procédé ou coupé actuellement, s’il vous plait contacter ",
			processMessage3: " ou le clavardage en ligne pour plus d’assistance.",
			companyLink: "%url%.",
			brandedEmail: getBrandInfo().email,
			length: "Veuillez indiquer une coupe inférieure à la longueur de coupe maximale (longueur minimale de 18, longueur maximale de 300).",
		},
	},
	es: {
		cutToSize: "Cortar a tamaño",
		primaryGrade: "Grado principal",
		thickness: "Espesor",
		width: "Ancho",
		length: "Largura",
		type: "Tipo",
		finish: "Acabado",
		apply: "Aplicar",
		back: "Regresar",
		select: "Seleccionar",
		cutTo: "Cortar a:",
		cutFrom: "Cortar de:",
		quantityPcs: "Cantidad, pieza(s)",
		quantityTooltipMessage:
			"Indique el tamaño final de corte y el número total de piezas necesarias para este artículo.",
		cutAttributes: {
			shape: {
				value: "cutShape",
				display: "Forma",
			},
			processing: {
				value: "processing",
				display: "Proceso",
			},
			finish: {
				value: "finish",
				display: "Acabado",
			},
			width: {
				value: "width",
				display: "Ancho",
			},
			length: {
				value: "length",
				display: "Longitud",
			},
			oneSide: {
				value: "oneSide",
				display: "Lado",
			},
			oneSideTolerance: {
				value: "oneSideTolerance",
				display: "Tolerancia unilateral",
			},
			tolerance: {
				value: "tolerance",
				display: "Tolerancia",
			},
			outsideDiameter: {
				value: "outsideDiameter",
				display: "Diámetro externo",
			},
			outsideTolerance: {
				value: "outsideTolerance",
				display: "Tolerancia exterior",
			},
			insideDiameter: {
				value: "insideDiameter",
				display: "Diámetro interno",
			},
			insideTolerance: {
				value: "insideTolerance",
				display: "Tolerancia interior",
			},
			longSide: {
				value: "longSide",
				display: "Lado largo",
			},
			shortSide: {
				value: "shortSide",
				display: "Lado corto",
			},
			widthLengthTolerance: {
				value: "widthLengthTolerance",
				display: "Tolerancia ancho/longitud",
			},
		},
		errors: {
			required: "Campo obligatorio",
			outsideDiameter: {
				lessThanPlateWidth:
					"El diámetro exterior solicitado debe ser inferior o igual al ancho de la placa en existencias menos 2 pulgadas para procesar el pedido",
				minimumForOxyFuel:
					"El diámetro debe ser mayor o igual a 3 pulgadas para las piezas cortadas con OxyFuel y Plasma",
				minimumForLaserCut:
					"El diámetro solicitado debe ser mayor o igual a 1 pulgada para los artículos cortados con láser",
			},
			insideDiameter: {
				lessThanOutside:
					"El diámetro interior solicitado debe ser inferior al diámetro exterior solicitado",
				twoTimesPlateThickness:
					"La diferencia entre el diámetro exterior y el interior debe ser dos veces (2X) el espesor de la placa",
				thickerWallSize:
					"Actualice el diámetro exterior o interior para obtener un tamaño de pared más grueso. El grosor mínimo de la pared del anillo (diferencia entre el diámetro exterior y el interior) es de 1",
			},
			oneSide: {
				lessThanPlateWidth:
					"El ancho solicitado debe ser igual o inferior al ancho en existencias menos 2 pulgadas para procesar el pedido",
			},
			sheet: {
				sheetWidth:
					"El ancho solicitado debe ser igual o inferior al ancho en existencias menos 2 pulgadas para procesar el pedido",
				sheetLength:
					"La longitud solicitada debe ser igual o inferior a la longitud de stock menos 2 pulgas para procesar el pedido",
			},
			longForm:
				"La longitud solicitada debe ser inferor o igual a la longitud minimal de la pieza",
			processMessage1: "Gracias por utilizar ",
			processMessage2:
				". Este articulo no puede ser cortado por el momento, por favor contáctenos en ",
			processMessage3: " o nuestro servicio de chat en vivo para asistencia.",
			companyLink: "%url%.",
			brandedEmail: getBrandInfo().email,
			length: "Especifique un corte inferior a la longitud máxima de corte (Longitud mínima 18, Longitud máxima 300)",
		},
	},
};
